import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kMatterArtifactAppliedTemplateUrlApiPath } from '@/agent-v2/artifact-templates/constants'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { z } from 'zod'

const AppliedTemplateUrlApiResponseSchema = z.object({
  matter_artifact_version_id: z.string().uuid(),
  download_url: z.string().min(1),
  expires_at: z.string(),
})

/**
 * Fetch a fresh signed download URL for a version's stored templated docx. Throws ApiError(404)
 * when no applied output exists for the version — callers treat that as "re-apply needed".
 */
export async function getMatterArtifactAppliedTemplateUrl(matterArtifactVersionId: string): Promise<{ downloadUrl: string }> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactAppliedTemplateUrlApiPath(matterArtifactVersionId)}`
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      // A 404 is an expected state (no stored output for this version) — no Sentry noise.
      if (response.status !== 404) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentry_transaction_id })
          Sentry.captureException(new Error(`Get applied template URL failed - status: ${response.status}`), {
            extra: {
              ...getResponseDebugContext(response, apiUrl),
              matterArtifactVersionId,
              responseData: parseError ? {} : data,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })
      }
      throw new ApiError(response.status, `Failed to get applied template URL: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Get applied template URL response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterArtifactVersionId,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid applied template URL response JSON')
    }

    const parsed = AppliedTemplateUrlApiResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Get applied template URL response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterArtifactVersionId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid applied template URL response shape', parsed.error.issues)
    }

    return { downloadUrl: parsed.data.download_url }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
